import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getClientSideContacts, getTeamCompanyCard } from '@/services/teamAssignments.service';
import { getPayslipPublication } from '@/services/compensation.service';
import PayslipPublicationCard, {
  PAYSLIP_PUBLICATION_QUERY_KEY,
} from '@/components/compensation/PayslipPublicationCard';
import ClientSideContactsPanel from '@/components/team-assignments/ClientSideContactsPanel';
import DocsSettingsForm from '@/app/timesheet/_components/form/DocsSettingsForm';
import CompanyEquipmentPanel from '@/components/equipment-requests/CompanyEquipmentPanel';
import {
  COMPANY_EQUIPMENT_ROLES,
  useCompanyEquipmentSummary,
} from '@/components/equipment-requests/useCompanyEquipmentOverview';
import EquipmentSettingsCard from '@/components/equipment-requests/EquipmentSettingsCard';
import ZazSettingsCard from '@/components/zaz/ZazSettingsCard';
import { ResourceHistory } from '@/components/resource-history';
import useJobPositions from '@/components/job-positions/useJobPositions';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';
import CompanyPaymentInstructionsForm from '../../_components/form/CompanyPaymentInstructionsForm';
import CompanyQuestionnaireForm from '../../_components/form/CompanyQuestionnaireForm';
import CompanyContractsPanel from './CompanyContractsPanel';
import CompanyDocumentsPanel from './CompanyDocumentsPanel';
import CompanyEmployeesPanel from './CompanyEmployeesPanel';
import CompanyJobPositionsPanel from './CompanyJobPositionsPanel';
import CompanyTeamPanel from './CompanyTeamPanel';

/**
 * What the details panel shows for each company tile besides the form: the
 * old page's tabs (they save on their own) and the employee overviews.
 *
 * @returns {{ before: Object<string, React.ReactNode>, after: Object<string, React.ReactNode> }}
 */
export default function useCompanyPanelExtras({
  company,
  companyId,
  formData,
  stats,
  access,
}) {
  const queryClient = useQueryClient();
  // Loads the team labels the tile summary reads.
  useTranslation(['teamAssignments', 'teamContacts']);

  // Same query key as TeamFirmCard and the header's team button: one request.
  const { data: team } = useQuery({
    queryKey: ['team-card', companyId],
    queryFn: () => getTeamCompanyCard(companyId),
    enabled: !!access.team && !!companyId,
    retry: false,
  });

  // Same query as the tile's panel: the summary reads it. A client of another
  // company gets a refusal and the panel says so.
  const { data: clientContacts } = useQuery({
    queryKey: ['client-side-contacts', companyId],
    queryFn: () => getClientSideContacts(companyId),
    enabled: !!access.clientContacts && !!companyId,
    retry: false,
  });

  // Same query as the tile's panel: the summary reads it.
  const { data: jobPositions } = useJobPositions(
    company?.applicationSettings?.isHr ? companyId : null
  );

  // Equipment overview: HR companies, roles that may ask for it; the server
  // answers 403 to anyone not linked to the company as such (the tile then
  // stays hidden). Counters only (no rows): the summary reads them.
  const roles = useAuthStore((state) => state.user?.roles);
  const mayAskEquipment =
    !!company?.applicationSettings?.isHr &&
    COMPANY_EQUIPMENT_ROLES.some((role) => roles?.includes(role));
  const { data: equipment, error: equipmentError } =
    useCompanyEquipmentSummary(companyId, { enabled: mayAskEquipment });
  const showEquipment =
    mayAskEquipment && ![401, 403].includes(equipmentError?.response?.status);

  // HLB only: the server refuses anyone else and the tile stays hidden. Same
  // query as the tile's panel: the summary reads it.
  const { data: payslipPublication } = useQuery({
    queryKey: [PAYSLIP_PUBLICATION_QUERY_KEY, companyId],
    queryFn: () => getPayslipPublication(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const showPayslips = !!payslipPublication?.months?.length;

  const after = {
    // Equipment request and ZAZ (request for hiring) for new employees (HR
    // companies); each saves on its own.
    hr: company?.applicationSettings?.isHr && access.docsSettings && (
      <>
        <EquipmentSettingsCard companyId={companyId} readOnly={!access.canEdit} />
        <ZazSettingsCard companyId={companyId} readOnly={!access.canEdit} />
      </>
    ),
    zaposleni: <CompanyEmployeesPanel companyId={companyId} stats={stats} />,
    ugovori: <CompanyContractsPanel companyId={companyId} stats={stats} />,
    radnaMesta: <CompanyJobPositionsPanel companyId={companyId} />,
    oprema: showEquipment && <CompanyEquipmentPanel companyId={companyId} />,
    listici: showPayslips && (
      <PayslipPublicationCard companyId={companyId} data={payslipPublication} />
    ),
    placanje: access.paymentInstructions && (
      <CompanyPaymentInstructionsForm
        initialValues={formData?.companyPaymentsSlips}
        formData={formData}
        isEdited
        companyId={companyId}
        readOnly={!access.canEdit}
      />
    ),
    upitnik: access.questionnaire && <CompanyQuestionnaireForm isEdited />,
    dokumenta: (
      <CompanyDocumentsPanel
        companyId={companyId}
        formData={formData}
        access={access}
      />
    ),
    // The old drawer's form, as a tile of its own. It saves on its own;
    // the tile summary follows the saved settings.
    sabloni: access.docsSettings && (
      <DocsSettingsForm
        cards
        companyId={companyId}
        isEdited
        onClose={() =>
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.company, companyId],
          })
        }
      />
    ),
    tim: access.team && <CompanyTeamPanel companyId={companyId} />,
    kontakti: access.clientContacts && <ClientSideContactsPanel companyId={companyId} />,
    istorija: (
      <ResourceHistory
        modelName="Company"
        documentId={companyId}
        variant="profile"
        createdAt={company?.createdAt}
      />
    ),
  };

  return {
    before: {},
    after,
    team,
    jobPositions,
    equipment,
    showEquipment,
    clientContacts,
    payslipPublication,
    showPayslips,
  };
}
